import { IconButton, useBreakpoints } from "@aurem/ui";
import { useUserData } from "graphql/user";
import { MenuIcon } from "lucide-react";
import { useI18n } from "providers/I18nProvider";
import { useMemo } from "react";
import { useCurrentApp } from "../../hooks/useCurrentApp";
import { GlobalSearchToggle } from "../GlobalSearchDialog";
import { NotificationCenter } from "../Notification";
import { ChatToggle } from "./ChatToggle";
import { LanguageCurrencyToggle } from "./LanguageCurrencyToggle";
import { PageTitle } from "./PageTitle";
import { TranslationToggle } from "./TranslationToggle";
import { UserDropdown } from "./UserDropdown";

type Props = {
  openOuterSidebar: () => void;
};

export const Topbar = ({ openOuterSidebar }: Props) => {
  const { t } = useI18n();
  const currentApp = useCurrentApp();
  const { isMobile } = useBreakpoints();
  const { user } = useUserData();
  const isAdminRoute = currentApp === "admin";

  // The burger opens LayoutOuterSidebar, which only renders for platform
  // admins. Hide it for everyone else (e.g. company admins) so it doesn't
  // open an empty drawer.
  const isPlatformAdmin = useMemo(() => {
    if (!user?.userRoles) return false;
    return user.userRoles.some((userRole) => userRole.role.isPlatform);
  }, [user]);

  return (
    <div className="bg-white ps-1.5 pe-4 sm:px-6 h-16 shrink-0 border-b border-neutral-500/20 z-50">
      <nav className="flex items-center justify-between h-full">
        <div className="flex items-center gap-2">
          {isMobile && isPlatformAdmin && (
            <IconButton
              variant="ghost"
              color="neutral"
              size="md"
              aria-label={t("apps.admin.navBar.menu")}
              onClick={openOuterSidebar}
            >
              <MenuIcon />
            </IconButton>
          )}
          <PageTitle />
        </div>

        <div className="flex items-center gap-4">
          {isAdminRoute && <GlobalSearchToggle />}
          <NotificationCenter />
          {!isMobile && isAdminRoute && <TranslationToggle />}
          {isAdminRoute && <ChatToggle />}
          <LanguageCurrencyToggle />
          <UserDropdown />
        </div>
      </nav>
    </div>
  );
};
